import {
  DealAction,
  DealStatus,
  useToast,
  type DealDispatcher,
} from '@pelando/components';
import { useRouter } from 'next/router';
import { useTranslation } from '@/presentation/hooks/useTranslation';
import { getTimeAgoString } from '@/presentation/services/time/getTimeAgo';
import getStampType from '@/presentation/services/deals/getStampType';
import getDealValue from '@/presentation/services/deals/getDealValue';
import { getDealCardVerifiedAuthorHeader } from '@/presentation/services/deals/getDealCardVerifiedAuthorHeader';
import {
  getDealUrl,
  getCouponsUrl,
  getCommentsSectionDealUrl,
} from '@/presentation/services/urls';
import useDealHeaderActionHandler from '@/presentation/components/DealHeader/hooks/useDealHeaderActionHandler';
import { actionHandlerMutations } from '@/presentation/pages/public/Deal/actionHandlerMutations';
import actionHandlerClickouts from '@/presentation/pages/secure/profile/components/actionHandlerClickouts';
import { StoreDeal } from '@/domain/entities/Store';
import { StoreDealsTabContentProps } from '..';
import { DealStoreCard } from './style';

type StoreDealCardProps = Pick<
  StoreDealsTabContentProps,
  'userOffersVote' | 'onVote' | 'onRemoveVote'
> & {
  deal: StoreDeal;
  cardPosition: number;
};

export const StoreDealCard = ({
  deal,
  userOffersVote,
  onRemoveVote,
  onVote,
  cardPosition,
}: StoreDealCardProps) => {
  const { push, locale = '' } = useRouter();
  const { t } = useTranslation('common', 'profile');
  const { showToast } = useToast();

  const storeLabel = t('store-label');

  const {
    user: author,
    status,
    kind,
    id,
    title,
    price,
    commentCount,
    store,
    image,
    timestamps,
    cashback,
  } = deal;

  const imageUrl = image?.url || '';
  const storeImageUrl = store?.image.url || '';
  const storeSlug = store?.slug || '';
  const storeName = store?.name || '';
  const temperature = Math.round(deal.temperature);
  const value = getDealValue(deal);
  const stamp = getStampType(deal);
  const dealUrl = getDealUrl({
    id,
    title,
  });
  const commentsDealUrl = getCommentsSectionDealUrl(dealUrl);
  const storeUrl = getCouponsUrl({
    storeSlug,
    locale,
  });
  const timestamp = getTimeAgoString(
    new Date(timestamps?.createdAt || ''),
    locale
  );
  const headerContent = getDealCardVerifiedAuthorHeader(author);

  const actionHandler = useDealHeaderActionHandler({
    deal: {
      id,
      title,
      kind,
      status,
      price,
    },
  });

  const handleDispath: DealDispatcher = ({ type, payload }) => {
    actionHandler(type as DealAction);
    actionHandlerMutations({
      action: { type, payload },
      onVote,
      onRemoveVote,
      showToast,
      eventPrefix: 'store_page',
    });
    actionHandlerClickouts({
      action: { type, payload },
      push,
      kind,
      status,
      dealId: deal.id,
      storeName: deal.store?.name,
      dealUrl,
      cardPosition,
      locale,
    });
  };

  return (
    <DealStoreCard
      dealUrl={dealUrl}
      commentsDealUrl={commentsDealUrl}
      commentCount={commentCount}
      key={id}
      dispatch={handleDispath}
      id={id}
      status={status as unknown as DealStatus}
      storeName={storeName}
      temperature={temperature}
      title={title}
      image={imageUrl}
      storeImage={storeImageUrl}
      timestamp={timestamp}
      storeLink={storeUrl}
      value={value}
      stampType={stamp}
      storeSlug={storeSlug}
      voteInfo={userOffersVote ? userOffersVote[deal.id] : undefined}
      headerContent={headerContent}
      storeText={storeLabel}
      locale={locale}
      cashbackPercentage={cashback?.percentage}
    />
  );
};
